// src/pages/dashboards/AdminDashboard.jsx
// Copy this complete code into your AdminDashboard.jsx file.
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  UsersIcon,
  AcademicCapIcon,
  BookOpenIcon,
  RectangleStackIcon,
  ClockIcon,
  UserPlusIcon,
  UserGroupIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  SparklesIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { FireLoader } from "../../components/Loader";
import { showError, showWarning } from "../../utils/alert";

// ✅ Socket import (same as used in Students page)
import { socket } from "../../services/socket";

// =========================
// HELPER: Extract data from various API response shapes
// =========================
const extractData = (response) => {
  const data = response?.data;
  if (!data) return { items: [], total: 0 };
  if (Array.isArray(data)) return { items: data, total: data.length };
  if (data.data && Array.isArray(data.data)) {
    return {
      items: data.data,
      total: data.pagination?.total || data.data.length,
    };
  }
  if (data.rows && Array.isArray(data.rows)) {
    return {
      items: data.rows,
      total: data.count || data.rows.length,
    };
  }
  const arrayProp = Object.values(data).find((val) => Array.isArray(val));
  if (arrayProp) return { items: arrayProp, total: arrayProp.length };
  return { items: [], total: 0 };
};

const TOUR_STORAGE_KEY = "pawaschool-admin-dashboard-tour-v1";

const tourSteps = [
  {
    target: "welcome",
    title: "Welcome to your dashboard",
    description:
      "This is your school command centre. You can see important information and reach daily management tools from here.",
  },
  {
    target: "stats",
    title: "Your school at a glance",
    description:
      "These live cards show student, teacher and class totals. Select any card to open its full record.",
  },
  {
    target: "activity",
    title: "Follow recent activity",
    description:
      "See the latest enrolments and other school updates without leaving the dashboard.",
  },
  {
    target: "quick-actions",
    title: "Get things done quickly",
    description:
      "Use these shortcuts to add students, teachers and classes, or manage your school subjects.",
  },
  {
    target: "overview",
    title: "Understand your school",
    description:
      "The overview gives you a simple visual breakdown of students, teachers and classes. You are ready to begin!",
  },
];

function DashboardTour({ step, onNext, onBack, onClose }) {
  const [position, setPosition] = useState(null);
  const currentStep = tourSteps[step];

  useEffect(() => {
    const target = document.querySelector(
      `[data-tour="${currentStep.target}"]`,
    );
    if (!target) return undefined;

    let animationFrame;
    let settleTimer;

    const previousStyles = {
      position: target.style.position,
      zIndex: target.style.zIndex,
      isolation: target.style.isolation,
      boxShadow: target.style.boxShadow,
      transition: target.style.transition,
      borderRadius: target.style.borderRadius,
      pointerEvents: target.style.pointerEvents,
    };

    // Lift the real dashboard section above the dark overlay. This keeps all
    // text, icons, cards and charts fully visible instead of showing a fake
    // empty spotlight rectangle.
    if (window.getComputedStyle(target).position === "static") {
      target.style.position = "relative";
    }
    target.style.zIndex = "100";
    target.style.isolation = "isolate";
    target.style.borderRadius = "16px";
    target.style.transition =
      "box-shadow 420ms cubic-bezier(0.22, 1, 0.36, 1), transform 420ms cubic-bezier(0.22, 1, 0.36, 1)";
    target.style.boxShadow =
      "0 0 0 4px rgba(255,255,255,0.98), 0 18px 48px rgba(15,23,42,0.30)";
    target.style.pointerEvents = "none";
    target.classList.add("dashboard-tour-target");

    const updatePosition = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const rect = target.getBoundingClientRect();
        const isMobile = window.innerWidth < 768;
        const margin = isMobile ? 12 : 20;
        const gap = 20;
        const tooltipWidth = isMobile
          ? window.innerWidth - margin * 2
          : Math.min(360, window.innerWidth - margin * 2);
        const tooltipHeight = 270;
        const spaces = {
          bottom: window.innerHeight - rect.bottom,
          top: rect.top,
          right: window.innerWidth - rect.right,
          left: rect.left,
        };

        let top;
        let left;

        // On phones the explanation stays in a compact bottom sheet and the
        // selected section is scrolled into the clear area above it.
        if (isMobile) {
          top = undefined;
          left = margin;
        } else if (spaces.right >= tooltipWidth + gap) {
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + gap;
        } else if (spaces.left >= tooltipWidth + gap) {
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - gap;
        } else if (spaces.bottom >= tooltipHeight + gap) {
          top = rect.bottom + gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
        } else {
          top = rect.top - tooltipHeight - gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
        }

        top = Math.max(margin, Math.min(top, window.innerHeight - tooltipHeight - margin));
        left = Math.max(margin, Math.min(left, window.innerWidth - tooltipWidth - margin));

        setPosition({
          top,
          left,
          width: tooltipWidth,
          isMobile,
          bottom: isMobile ? margin : undefined,
        });
      });
    };

    setPosition(null);
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    updatePosition();
    settleTimer = window.setTimeout(updatePosition, 450);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, { passive: true });
    return () => {
      window.clearTimeout(settleTimer);
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      target.classList.remove("dashboard-tour-target");
      Object.assign(target.style, previousStyles);
    };
  }, [currentStep.target]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onNext();
      if (event.key === "ArrowLeft" && step > 0) onBack();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onBack, onClose, onNext, step]);

  if (!position) return null;

  return (
    <>
      <div className="fixed inset-0 z-[90] bg-slate-950/70 backdrop-blur-[1px] animate-[tour-fade_300ms_ease-out]" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-tour-title"
        className={`fixed z-[110] overflow-hidden border border-gray-100 bg-white shadow-xl animate-[tour-enter_420ms_cubic-bezier(0.22,1,0.36,1)] ${
          position.isMobile ? "rounded-2xl" : "rounded-xl"
        }`}
        style={{
          top: position.top,
          bottom: position.bottom,
          left: position.left,
          width: position.width,
        }}
      >
        <div className="h-1.5 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500"
            style={{ width: `${((step + 1) / tourSteps.length) * 100}%` }}
          />
        </div>
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
              <SparklesIcon className="w-5 h-5" />
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 -mr-2 -mt-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close tour"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-primary-500">
            Step {step + 1} of {tourSteps.length}
          </p>
          <h2 id="dashboard-tour-title" className="mt-1 text-xl font-bold text-gray-900">
            {currentStep.title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-gray-500">
            {currentStep.description}
          </p>
          <div className="mt-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={step === 0 ? onClose : onBack}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
            >
              {step === 0 ? (
                "Skip tour"
              ) : (
                <><ChevronLeftIcon className="w-4 h-4" /> Back</>
              )}
            </button>
            <button
              type="button"
              onClick={onNext}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            >
              {step === tourSteps.length - 1 ? "Finish" : "Next"}
              {step < tourSteps.length - 1 && (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, hasFeature, getPlanLimit, planAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    totalClasses: 0,
    totalTeachers: 0,
    maleStudents: 0,
    femaleStudents: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [tourStep, setTourStep] = useState(null);

  // Keep onboarding completion separate for every admin/school. A single
  // global key would incorrectly hide the tour when another school logs in
  // on the same browser.
  const tourUserId = user?.uuid || user?.id || user?.email;
  const tourSchoolId = user?.school_id || user?.schoolId;
  const tourIdentity = [tourUserId, tourSchoolId].filter(Boolean).join(":");
  const tourStorageKey = tourIdentity
    ? `${TOUR_STORAGE_KEY}:${tourIdentity}`
    : null;

  const fetchDashboardDataRef = useRef();

  // ─── Fetch dashboard data with optional background mode ───
  const fetchDashboardData = useCallback(
    async (options = { background: false }) => {
      try {
        if (!options.background) {
          setLoading(true);
        }

        const [
          studentsResponse,
          activeStudentsResponse,
          classesResponse,
          teachersResponse,
        ] = await Promise.all([
          apiClient.get("/students"),
          apiClient.get("/students?status=active"),
          apiClient.get("/classes"),
          apiClient.get("/teachers"),
        ]);

        const students = extractData(studentsResponse);
        const activeStudentsData = extractData(activeStudentsResponse);
        const classes = extractData(classesResponse);
        const teachers = extractData(teachersResponse);

        const studentsData = students.items;
        const totalStudents = students.total;
        let activeStudents = activeStudentsData.total;
        if (activeStudents === 0 && studentsData.length > 0) {
          activeStudents = studentsData.filter(
            (s) => s.status === "active" || s.is_active === true,
          ).length;
        }
        const maleStudents = studentsData.filter(
          (s) => s.gender?.toLowerCase() === "male",
        ).length;
        const femaleStudents = studentsData.filter(
          (s) => s.gender?.toLowerCase() === "female",
        ).length;

        setStats({
          totalStudents,
          activeStudents,
          totalClasses: classes.total,
          totalTeachers: teachers.total,
          maleStudents,
          femaleStudents,
        });

        // Build recent activities
        const activities = [];
        const sortedStudents = [...studentsData].sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || 0);
          const dateB = new Date(b.created_at || b.createdAt || 0);
          return dateB - dateA;
        });

        sortedStudents.slice(0, 3).forEach((student) => {
          const fullName = `${student.first_name || student.firstName || ""} ${
            student.last_name || student.lastName || ""
          }`.trim();
          if (fullName) {
            activities.push({
              id: `enroll-${student.id}`,
              user: fullName,
              action: "enrolled as new student",
              time: student.created_at || student.createdAt,
              type: "enrollment",
              icon: UserPlusIcon,
              iconColor: "text-purple-500",
              bgColor: "bg-purple-50",
            });
          }
        });

        if (teachers.items.length > 0) {
          const recentTeacher = teachers.items[0];
          const fullName =
            `${recentTeacher.first_name || recentTeacher.firstName || ""} ${
              recentTeacher.last_name || recentTeacher.lastName || ""
            }`.trim();
          activities.push({
            id: `teacher-${recentTeacher.id}`,
            user: fullName || "Teacher",
            action: "joined as new teacher",
            time:
              recentTeacher.created_at ||
              recentTeacher.createdAt ||
              new Date().toISOString(),
            type: "teacher",
            icon: AcademicCapIcon,
            iconColor: "text-green-500",
            bgColor: "bg-green-50",
          });
        }

        if (classes.items.length > 0) {
          activities.push({
            id: `class-${classes.items[0].id}`,
            user: "System",
            action: `${classes.items.length} classes available`,
            time: new Date().toISOString(),
            type: "classes",
            icon: BookOpenIcon,
            iconColor: "text-blue-500",
            bgColor: "bg-blue-50",
          });
        }

        setRecentActivities(activities.slice(0, 4));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        if (error.response?.status !== 401 && !options.background) {
          showError({
            title: "Dashboard Error",
            text: error.response?.data?.message || "Failed to load dashboard data.",
          });
        }
      } finally {
        if (!options.background) {
          setLoading(false);
        }
      }
    },
    [],
  );

  // ─── Store fetch function in ref ───
  useEffect(() => {
    fetchDashboardDataRef.current = fetchDashboardData;
  }, [fetchDashboardData]);

  // ─── Initial load ───
  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || !tourStorageKey) return undefined;

    const timer = window.setTimeout(() => {
      if (!localStorage.getItem(tourStorageKey)) {
        setTourStep(0);
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [loading, tourStorageKey]);

  const closeTour = useCallback(() => {
    if (tourStorageKey) {
      localStorage.setItem(tourStorageKey, "completed");
    }
    setTourStep(null);
  }, [tourStorageKey]);

  const nextTourStep = useCallback(() => {
    setTourStep((current) => {
      if (current === null || current >= tourSteps.length - 1) {
        if (tourStorageKey) {
          localStorage.setItem(tourStorageKey, "completed");
        }
        return null;
      }
      return current + 1;
    });
  }, [tourStorageKey]);

  const previousTourStep = useCallback(() => {
    setTourStep((current) => Math.max(0, (current ?? 0) - 1));
  }, []);

  // ─── Socket.IO real‑time listener (background updates) ───
  useEffect(() => {
    // Handler for student changes (same event used in Students page)
    const handleStudentUpdate = (event) => {
      console.log("📡 Dashboard student update received:", event);
      if (fetchDashboardDataRef.current) {
        fetchDashboardDataRef.current({ background: true });
      }
    };

    // Optional: listen to teacher and class updates as well
    const handleOtherUpdate = () => {
      console.log("📡 Dashboard other update received");
      if (fetchDashboardDataRef.current) {
        fetchDashboardDataRef.current({ background: true });
      }
    };

    socket.on("student:updated", handleStudentUpdate);
    socket.on("teacher:updated", handleOtherUpdate);
    socket.on("class:updated", handleOtherUpdate);

    return () => {
      socket.off("student:updated", handleStudentUpdate);
      socket.off("teacher:updated", handleOtherUpdate);
      socket.off("class:updated", handleOtherUpdate);
    };
  }, []);

  // ─── Handlers ───
  const openPlanAction = ({ feature, resource, current = 0, path }) => {
    if (!hasFeature(feature)) {
      return showWarning({
        title: "Plan Upgrade Required",
        text: `${feature.replaceAll("_", " ")} is not included in the ${planAccess?.plan?.name || "current"} plan.`,
      });
    }

    if (resource) {
      const unlimited = planAccess?.limits?.[`unlimited_${resource}`] === true;
      const limit = getPlanLimit(resource);
      if (!unlimited && limit !== null && current >= limit) {
        return showWarning({
          title: `${resource.charAt(0).toUpperCase()}${resource.slice(1)} Limit Reached`,
          text: `Your plan allows up to ${limit} ${resource}. Upgrade your plan to add another one.`,
        });
      }
    }

    return navigate(path);
  };

  const handleAddStudent = () => openPlanAction({ feature: "students", resource: "students", current: stats.totalStudents, path: "/app/students/add" });
  const handleAddTeacher = () => openPlanAction({ feature: "teachers", resource: "teachers", current: stats.totalTeachers, path: "/app/teachers/add" });
  const handleAddClass = () => openPlanAction({ feature: "classes", path: "/app/classes/add" });
  const handleSubjects = () => openPlanAction({ feature: "subjects", path: "/app/subjects" });
  const handleViewAllActivities = () => navigate("/app/students");

  const timeAgo = (date) => {
    if (!date) return "Just now";
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  };

  const adminName = user?.first_name
    ? `${user.first_name} ${user.last_name || ""}`
    : "Admin";

  // Quick actions (unchanged)
  const quickActions = [
    {
      id: "add-student",
      label: "Add Student",
      description: "Enroll a new student",
      icon: UserPlusIcon,
      color: "from-blue-50 to-blue-100",
      iconBg: "from-blue-500 to-blue-600",
      shadowColor: "shadow-blue-200/50",
      feature: "students",
      onClick: handleAddStudent,
    },
    {
      id: "add-teacher",
      label: "Add Teacher",
      description: "Add a new teacher",
      icon: AcademicCapIcon,
      color: "from-green-50 to-green-100",
      iconBg: "from-green-500 to-green-600",
      shadowColor: "shadow-green-200/50",
      feature: "teachers",
      onClick: handleAddTeacher,
    },
    {
      id: "add-class",
      label: "Add Class",
      description: "Create a new class",
      icon: BookOpenIcon,
      color: "from-purple-50 to-purple-100",
      iconBg: "from-purple-500 to-purple-600",
      shadowColor: "shadow-purple-200/50",
      feature: "classes",
      onClick: handleAddClass,
    },
    {
      id: "subjects",
      label: "Subjects",
      description: "Manage school subjects",
      feature: "subjects",
      icon: RectangleStackIcon,
      color: "from-amber-50 to-orange-100",
      iconBg: "from-amber-500 to-orange-600",
      shadowColor: "shadow-amber-200/50",
      onClick: handleSubjects,
    },
  ];

  // Pie chart data (unchanged)
  const pieData = [
    { name: "Students", value: stats.totalStudents, color: "#3b82f6" },
    { name: "Teachers", value: stats.totalTeachers, color: "#10b981" },
    { name: "Classes", value: stats.totalClasses, color: "#f59e0b" },
  ];
  const totalValue = pieData.reduce((sum, item) => sum + item.value, 0);

  const AnimatedPieChart = () => {
    const [hovered, setHovered] = useState(null);
    const radius = 82;
    const circumference = 2 * Math.PI * radius;
    return (
      <div className="flex flex-col items-center">
        <div className="relative w-[200px] h-[200px] sm:w-[230px] sm:h-[230px]">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 230 230"
            className="transform -rotate-90"
          >
            <circle
              cx="115"
              cy="115"
              r={radius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="30"
            />
            {pieData.map((item, index) => {
              if (item.value === 0 || totalValue === 0) return null;
              const percentage = item.value / totalValue;
              const dashLength = percentage * circumference;
              let offset = 0;
              for (let i = 0; i < index; i++) {
                if (pieData[i].value > 0) {
                  offset -= (pieData[i].value / totalValue) * circumference;
                }
              }
              return (
                <circle
                  key={index}
                  cx="115"
                  cy="115"
                  r={radius}
                  fill="none"
                  stroke={item.color}
                  strokeWidth="30"
                  strokeDasharray={`${dashLength} ${circumference}`}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  style={{
                    transition: `stroke-dashoffset 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.1}s`,
                  }}
                  onMouseEnter={() => setHovered(item)}
                  onMouseLeave={() => setHovered(null)}
                  className="cursor-pointer hover:stroke-[34] transition-all duration-200"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-3xl sm:text-4xl font-bold text-primary-700">
              {totalValue}
            </div>
            <div className="text-xs sm:text-sm text-primary-400 -mt-1">
              School Total
            </div>
          </div>
          {hovered && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl px-4 py-3 border border-primary-100 z-20 pointer-events-none">
              <div className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: hovered.color }}
                />
                <div>
                  <p className="font-semibold text-primary-700 text-base">
                    {hovered.name}
                  </p>
                  <p className="text-2xl font-bold text-primary-600">
                    {hovered.value}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6 w-full max-w-[260px]">
          {pieData.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center cursor-pointer hover:scale-105 transition-transform"
              onMouseEnter={() => setHovered(item)}
              onMouseLeave={() => setHovered(null)}
            >
              <div
                className="w-4 h-4 rounded-full mb-2"
                style={{ backgroundColor: item.color }}
              />
              <p className="text-xs sm:text-sm font-medium text-primary-700">
                {item.name}
              </p>
              <p className="text-lg sm:text-xl font-semibold text-primary-600">
                {item.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading)
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;

  return (
    <>
      <style>{`
        @keyframes wave {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(15deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-15deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes tour-enter {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes tour-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes tour-highlight {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(255,255,255,0.98), 0 18px 48px rgba(15,23,42,0.26);
          }
          50% {
            box-shadow: 0 0 0 7px rgba(99,102,241,0.35), 0 22px 58px rgba(15,23,42,0.36);
          }
        }
        .dashboard-tour-target {
          animation: tour-highlight 1.8s ease-in-out infinite;
        }
        .wave-emoji {
          display: inline-block;
          animation: wave 1.5s ease-in-out infinite;
          transform-origin: 70% 70%;
        }
        .responsive-padding {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }
        @media (min-width: 640px) {
          .responsive-padding {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
        }
        @media (min-width: 1024px) {
          .responsive-padding {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8 responsive-padding">
        {/* Header */}
        <div data-tour="welcome" className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 shadow-lg p-5 sm:p-8 text-white">
          <div className="absolute right-0 top-0 opacity-10">
            <BuildingOfficeIcon className="w-32 sm:w-48 h-32 sm:h-48" />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold leading-tight">
                Welcome, {adminName}
                <span
                  className="wave-emoji ml-2"
                  style={{ fontSize: "inherit" }}
                >
                  👋
                </span>
              </h1>
              <p className="text-sm sm:text-base text-white/80 mt-1">
                Here's what's happening at your school.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div data-tour="stats" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div
            className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-blue-200 transition-colors cursor-pointer group"
            onClick={() => navigate("/app/students")}
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                <UsersIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">
                  Total Students
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">
                  {stats.totalStudents}
                </p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">
                All enrolled students
              </span>
            </div>
          </div>

          <div
            className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-green-200 transition-colors cursor-pointer group"
            onClick={() => navigate("/app/students?status=active")}
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors">
                <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">
                  Active Students
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">
                  {stats.activeStudents}
                </p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">
                Currently active
              </span>
            </div>
          </div>

          <div
            className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-purple-200 transition-colors cursor-pointer group"
            onClick={() => navigate("/app/teachers")}
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors">
                <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">
                  Total Teachers
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">
                  {stats.totalTeachers}
                </p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">
                Teaching staff
              </span>
            </div>
          </div>

          <div
            className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-orange-200 transition-colors cursor-pointer group"
            onClick={() => navigate("/app/classes")}
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-orange-50 text-orange-600 group-hover:bg-orange-100 transition-colors">
                <BriefcaseIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">
                  Total Classes
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">
                  {stats.totalClasses}
                </p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">
                Available classes
              </span>
            </div>
          </div>
        </div>

        {/* Two‑column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div data-tour="activity" className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 h-full">
              <div className="flex items-center justify-between mb-4 rounded-lg">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Recent Activity
                </h3>
                <button
                  onClick={handleViewAllActivities}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium touch-manipulation"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center gap-3 p-2 sm:p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${activity.bgColor || "bg-gray-100"} flex items-center justify-center flex-shrink-0`}
                      >
                        <activity.icon
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${activity.iconColor || "text-gray-500"}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-700 break-words">
                          <span className="font-medium">{activity.user}</span>{" "}
                          {activity.action}
                        </p>
                        <p className="text-xs text-gray-400">
                          {timeAgo(activity.time)}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-[10px] sm:text-xs rounded-full whitespace-nowrap ${
                          activity.type === "enrollment"
                            ? "bg-purple-50 text-purple-600"
                            : activity.type === "teacher"
                              ? "bg-green-50 text-green-600"
                              : activity.type === "classes"
                                ? "bg-blue-50 text-blue-600"
                                : "bg-gray-50 text-gray-600"
                        } capitalize`}
                      >
                        {activity.type}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions + Pie */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 h-full flex flex-col">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 rounded-lg">
                Quick Actions
              </h3>
              <div data-tour="quick-actions" className="space-y-3 flex-1 rounded-xl">
                {quickActions.map((action) => {
                  const locked = !hasFeature(action.feature);
                  const studentLimitReached =
                    action.id === "add-student" &&
                    planAccess?.limits?.unlimited_students !== true &&
                    getPlanLimit("students") !== null &&
                    stats.totalStudents >= getPlanLimit("students");
                  const teacherLimitReached =
                    action.id === "add-teacher" &&
                    planAccess?.limits?.unlimited_teachers !== true &&
                    getPlanLimit("teachers") !== null &&
                    stats.totalTeachers >= getPlanLimit("teachers");
                  const limitReached = studentLimitReached || teacherLimitReached;
                  const unavailable = locked || limitReached;
                  return (
                  <button
                    key={action.id}
                    onClick={action.onClick}
                    className={`w-full p-4 bg-gradient-to-br rounded-xl transition-all group flex items-center gap-4 ${unavailable ? "opacity-70 border border-amber-200" : "hover:from-opacity-100 hover:to-opacity-100"}`}
                    style={{
                      background: `linear-gradient(to bottom right, ${action.color.split(" ")[0]}, ${action.color.split(" ")[1] || action.color.split(" ")[0]})`,
                    }}
                  >
                    <div
                      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.iconBg} flex items-center justify-center shadow-lg ${action.shadowColor} flex-shrink-0 group-hover:scale-110 transition-transform`}
                    >
                      <action.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-primary-700">
                        {action.label}
                      </p>
                      <p className="text-xs text-primary-400">
                        {locked
                          ? "Not included in current plan"
                          : limitReached
                            ? "Plan limit reached"
                            : action.description}
                      </p>
                    </div>
                    {unavailable ? <LockClosedIcon className="w-4 h-4 text-amber-500" /> : <ArrowRightIcon className="w-4 h-4 text-primary-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />}
                  </button>
                  );
                })}
              </div>
              <div data-tour="overview" className="mt-6 pt-6 border-t border-gray-100 rounded-xl">
                <h4 className="text-sm font-semibold text-gray-600 mb-4 text-center rounded-lg">
                  School Overview
                </h4>
                <AnimatedPieChart />
              </div>
            </div>
          </div>
        </div>
      </div>
      {tourStep !== null && (
        <DashboardTour
          step={tourStep}
          onNext={nextTourStep}
          onBack={previousTourStep}
          onClose={closeTour}
        />
      )}
    </>
  );
}